import { useState, useEffect, useRef } from 'react';

interface PhotoAnnotationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (description: string) => void;
  initialValue?: string;
  photoName: string;
}

export function PhotoAnnotationModal({
  isOpen,
  onClose,
  onSave,
  initialValue = '',
  photoName,
}: PhotoAnnotationModalProps) {
  const [description, setDescription] = useState(initialValue);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Reset description when modal opens with new initial value
  useEffect(() => {
    if (isOpen) {
      setDescription(initialValue);
    }
  }, [isOpen, initialValue]);

  // Handle keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open and handle visual viewport for mobile keyboard
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';

      // Scroll textarea into view when focused (helps with mobile keyboard)
      const handleFocus = () => {
        setTimeout(() => {
          textareaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      };

      textareaRef.current?.addEventListener('focus', handleFocus);
      const currentRef = textareaRef.current;

      return () => {
        document.body.style.overflow = '';
        currentRef?.removeEventListener('focus', handleFocus);
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSave = () => {
    onSave(description.trim());
    onClose();
  };

  const hasNotes = initialValue.trim().length > 0;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 overflow-y-auto"
      onClick={onClose}
    >
      {/* Container that allows scrolling and positions modal near top on mobile */}
      <div className="min-h-full flex items-start sm:items-center justify-center p-4 pt-[10vh] sm:pt-4">
        <div
          className="bg-white rounded-lg shadow-xl w-full max-w-md"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-secondary-200">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-secondary-900">
                {hasNotes ? 'Edit Notes' : 'Add Notes'}
              </h2>
              <p className="text-sm text-secondary-500 truncate" title={photoName}>
                {photoName}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-secondary-100 transition-colors"
              aria-label="Close"
            >
              <svg className="w-5 h-5 text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="p-4">
            <textarea
              ref={textareaRef}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add notes or a caption for this photo..."
              className="w-full h-32 px-3 py-2 border border-secondary-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              autoFocus
            />
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-4 border-t border-secondary-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-secondary-700 hover:bg-secondary-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
